import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Radio, Users, Signal, AlertCircle, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents, mockStreamLogs } from '@/lib/mockData';
import { formatNumber } from '@/lib/utils';

export function AdminStreamMonitor() {
  const { eventId } = useParams();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];

  return (
    <AdminLayout>
      <div className="p-8">
        <Link to={`/admin/events/${eventId}`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Event
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Stream Monitor</h1>
            <p className="text-muted-foreground">{event.title}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-green-500 font-semibold">LIVE</span>
          </div>
        </div>

        {/* Stream Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Radio className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-xl font-bold text-green-500">Live</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Viewers</p>
                <p className="text-xl font-bold text-white">{formatNumber(2547)}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Peak Viewers</p>
                <p className="text-xl font-bold text-white">{formatNumber(3842)}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Signal className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Stream Quality</p>
                <p className="text-xl font-bold text-white">Excellent</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Stream Preview */}
        <Card className="bg-card border-border overflow-hidden mb-8">
          <div className="aspect-video relative bg-black">
            <img 
              src={event.coverImage || event.image} 
              alt="Stream preview"
              className="w-full h-full object-cover opacity-70"
            />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="px-3 py-1 bg-red-600 text-white text-sm font-semibold rounded-full flex items-center gap-1">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                LIVE
              </span>
              <span className="px-3 py-1 bg-black/50 text-white text-sm rounded-full">
                2,547 watching
              </span>
            </div>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex items-center gap-4 text-sm text-white">
                <span>Duration: 01:45:32</span>
                <span>Bitrate: 6000 kbps</span>
                <span>Resolution: 1080p60</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Stream Log */}
        <Card className="bg-card border-border p-6">
          <h2 className="text-xl font-bold text-white mb-4">Stream Log</h2>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {mockStreamLogs.map(log => (
              <div key={log.id} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
                {log.type === 'info' && <CheckCircle className="w-5 h-5 text-primary mt-0.5" />}
                {log.type === 'warning' && <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />}
                {log.type === 'error' && <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />}
                <div className="flex-1">
                  <p className="text-white">{log.message}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminStreamMonitor;
